import {
  APPRAISAL_OPEX_DEFAULTS,
  INITIAL_COMMERCIAL_UNITS,
  INITIAL_HMO_ROOMS,
  commercialUnitOpexDefault,
  isCoWorkStrategy,
  isServicedAccommodationStrategy,
} from '@/components/deals/appraisal/appraisal-data'

const DEFAULT_BRRR_STATE = {
  exitMode: 'Hold',
  propertyType: 'Flat',
  bedroomCount: '',
  valuationMethod: 'Comparable',
  suggestedGdv: '',
  adoptedGdv: '',
  suggestedSqft: '',
  adoptedSqft: '',
  internalArea: '',
  operatingModel: 'AST',
  suggestedMonthlyRent: '',
  adoptedMonthlyRent: '',
  astOpexMode: 'percentage',
  astOpexPercent: APPRAISAL_OPEX_DEFAULTS.leasedOrAst,
  astAnnualOperatingCost: '',
  suggestedAdr: '',
  adoptedAdr: '',
  occupancy: '75',
  opexPercent: APPRAISAL_OPEX_DEFAULTS.servicedAccommodation,
}

const DEFAULT_HMO_STATE = {
  rooms: INITIAL_HMO_ROOMS,
  valuationMethod: 'HMO Comparable',
  propertyType: 'Flat',
  constructionDate: 'Pre 1914',
  finishQuality: 'Very High',
  outdoorSpace: 'None',
  hasLivingRoom: true,
  hasParking: false,
  hmoSuggestedValue: '',
  hmoAdoptedValue: '',
  suggestedSqft: '',
  adoptedSqft: '',
  internalArea: '',
  adoptedEndValue: '',
  opexPercent: APPRAISAL_OPEX_DEFAULTS.hmo,
  yieldPercent: '8',
}

const DEFAULT_COMMERCIAL_STATE = {
  units: INITIAL_COMMERCIAL_UNITS,
}

const HMO_TYPE_ALIASES = {
  'double shared bath': 'double shared',
  'single shared bath': 'single shared',
}

const COMMERCIAL_TYPE_ALIASES = {
  office: 'offices',
  'industrial / warehouse': 'industrial',
}

const BRRR_MARKET_VALUE_PAIRS = [
  ['suggestedGdv', 'adoptedGdv'],
  ['suggestedSqft', 'adoptedSqft'],
  ['suggestedMonthlyRent', 'adoptedMonthlyRent'],
  ['suggestedAdr', 'adoptedAdr'],
]

export function buildMarketAwareAppraisalStates({
  currentInputs,
  deal,
  enrichment,
  marketData,
  selectedStrategy,
  subjectComparables,
}) {
  const scheme = currentInputs?.scheme ?? {}
  const areaData = marketDataForPostcode(marketData, deal?.postcodeArea)
  const brrrInput = scheme.brrr ?? {}
  const hmoInput = scheme.hmo ?? {}
  const commercialInput = scheme.commercial ?? {}
  const hasPersistedCommercialUnits = Array.isArray(commercialInput.units)
  const isCoWork = isCoWorkStrategy(selectedStrategy)
  const isServicedAccommodation = isServicedAccommodationStrategy(selectedStrategy)
  const residentialType = initialResidentialType(enrichment, brrrInput.propertyType)
  const brrrState = {
    ...DEFAULT_BRRR_STATE,
    operatingModel: isServicedAccommodation ? 'SA' : DEFAULT_BRRR_STATE.operatingModel,
    internalArea: enrichment?.enrichment?.approxSizeSqFt ?? '',
    ...brrrInput,
    propertyType: residentialType,
    bedroomCount: initialBedroomCount(enrichment, brrrInput, residentialType),
  }
  const hmoState = {
    ...DEFAULT_HMO_STATE,
    internalArea: enrichment?.enrichment?.approxSizeSqFt ?? '',
    ...hmoInput,
    rooms: rowsFromInput(hmoInput.rooms, INITIAL_HMO_ROOMS),
  }
  const commercialState = {
    ...DEFAULT_COMMERCIAL_STATE,
    ...commercialInput,
    units: hasPersistedCommercialUnits
      ? rowsFromInput(commercialInput.units, INITIAL_COMMERCIAL_UNITS)
        .map((unit) => withCommercialUnitOpexDefault(unit, isCoWork))
      : incomeScheduleUnits(enrichment?.units, areaData, isCoWork),
  }

  return {
    brrr: applyBrrrBaselines(
      brrrState,
      brrrInput,
      areaData,
      residentialSubjectComparable(subjectComparables, enrichment, brrrState),
    ),
    hmo: applyHmoBaselines(hmoState, hmoInput, areaData),
    commercial: applyCommercialBaselines(
      commercialState,
      commercialInput,
      areaData,
      subjectComparables,
    ),
  }
}

export function buildCommercialSchemeFromIncomeSchedule({
  enrichment,
  marketData,
  postcodeArea,
  selectedStrategy,
  subjectComparables,
}) {
  const areaData = marketDataForPostcode(marketData, postcodeArea)
  const isCoWork = isCoWorkStrategy(selectedStrategy)
  const state = {
    ...DEFAULT_COMMERCIAL_STATE,
    units: incomeScheduleUnits(enrichment?.units, areaData, isCoWork),
  }

  return applyCommercialBaselines(state, {}, areaData, subjectComparables)
}

export function commercialSchemeMatchesIncomeSchedule(appraisalUnits, incomeUnits) {
  const schemeUnits = Array.isArray(appraisalUnits) ? appraisalUnits : []
  const scheduleUnits = Array.isArray(incomeUnits) ? incomeUnits : []

  if (scheduleUnits.length === 0) {
    return !schemeUnits.some((unit) => unit.sourceUnitId)
  }
  if (schemeUnits.length !== scheduleUnits.length) return false

  return scheduleUnits.every((sourceUnit, index) => {
    const appraisalUnit = schemeUnits[index]

    return String(appraisalUnit?.sourceUnitId ?? '') === String(sourceUnit.id ?? '') &&
      String(appraisalUnit?.sourceUpdatedAt ?? '') === String(sourceUnit.updatedAt ?? '')
  })
}

export function refreshBrrrMarketData(
  previousState,
  nextState,
  marketData,
  postcodeArea,
  subjectComparable = null,
) {
  if (
    previousState.propertyType === nextState.propertyType &&
    previousState.bedroomCount === nextState.bedroomCount
  ) return nextState

  const areaData = marketDataForPostcode(marketData, postcodeArea)
  const refreshed = applyBrrrBaselines(
    nextState,
    nextState,
    areaData,
    subjectComparable,
  )

  return preserveEditedAdoptedValues(previousState, refreshed, BRRR_MARKET_VALUE_PAIRS)
}

export function applyBrrrSubjectComparable(
  currentState,
  marketData,
  postcodeArea,
  subjectComparables,
) {
  const areaData = marketDataForPostcode(marketData, postcodeArea)
  const refreshed = applyBrrrBaselines(
    currentState,
    currentState,
    areaData,
    appraisalSubjectComparable(subjectComparables),
  )

  return preserveEditedAdoptedValues(currentState, refreshed, BRRR_MARKET_VALUE_PAIRS)
}

export function appraisalSubjectComparable(subjectComparables) {
  const comparable = (subjectComparables?.units ?? []).find((unit) => (
    unit.unitId === 'appraisal-profile' &&
    unit.status === 'supported' &&
    positiveNumber(unit.indicatedValue)
  ))

  return comparable ?? null
}

export function refreshHmoMarketData(previousState, nextState, marketData, postcodeArea) {
  if (rowTypeSignature(previousState.rooms) === rowTypeSignature(nextState.rooms)) return nextState

  const areaData = marketDataForPostcode(marketData, postcodeArea)
  const refreshed = applyHmoBaselines(nextState, nextState, areaData)
  const previousRooms = new Map((previousState.rooms ?? []).map((room) => [room.id, room]))

  return {
    ...refreshed,
    rooms: refreshed.rooms.map((room) => preserveEditedRoomRent(previousRooms.get(room.id), room)),
  }
}

export function refreshCommercialMarketData(
  previousState,
  nextState,
  marketData,
  postcodeArea,
  subjectComparables = null,
) {
  if (rowTypeSignature(previousState.units) === rowTypeSignature(nextState.units)) return nextState

  const areaData = marketDataForPostcode(marketData, postcodeArea)
  const refreshed = applyCommercialBaselines(
    nextState,
    nextState,
    areaData,
    subjectComparables,
  )
  const previousUnits = new Map((previousState.units ?? []).map((unit) => [unit.id, unit]))

  return {
    ...refreshed,
    units: refreshed.units.map((unit) => preserveEditedUnitValues(previousUnits.get(unit.id), unit)),
  }
}

function applyBrrrBaselines(state, input, areaData, subjectComparable = null) {
  const valueRow = findRow(areaData.residentialValue, residentialValueType(state.propertyType))
  const rentRow = findResidentialRentRow(
    areaData.residentialRent,
    state.bedroomCount,
    state.propertyType,
  )
  const suggestions = {
    suggestedGdv: subjectComparable
      ? marketValue(subjectComparable.indicatedValue)
      : suggestedResidentialGdv(valueRow, state.internalArea),
    suggestedSqft: subjectComparable
      ? marketValue(subjectComparable.pricePerSqFt)
      : marketValue(valueRow?.sqft),
    suggestedMonthlyRent: marketValue(rentRow?.rent),
    suggestedAdr: '',
  }

  return {
    ...applySuggestedAndAdopted(state, input, suggestions, BRRR_MARKET_VALUE_PAIRS),
    comparableSource: comparableSource(subjectComparable),
  }
}

function applyHmoBaselines(state, input, areaData) {
  const valueRow = findRow(areaData.residentialValue, residentialValueType(state.propertyType))

  return {
    ...state,
    hmoSuggestedValue: '',
    hmoAdoptedValue: adoptedValue(input, 'hmoAdoptedValue', ''),
    suggestedSqft: marketValue(valueRow?.sqft),
    adoptedSqft: adoptedValue(input, 'adoptedSqft', marketValue(valueRow?.sqft)),
    rooms: state.rooms.map((room, index) => {
      const rowInput = input.rooms?.[index] ?? {}
      const hmoRow = findRow(areaData.hmo, hmoType(room.type))
      const suggestedRent = marketValue(hmoRow?.rent)

      return {
        ...room,
        suggestedRent,
        adoptedRent: adoptedValue(rowInput, 'adoptedRent', suggestedRent),
      }
    }),
  }
}

function applyCommercialBaselines(state, input, areaData, subjectComparables = null) {
  return {
    ...state,
    units: state.units.map((unit, index) => {
      const unitInput = input.units?.[index] ?? {}
      const commercialRow = findRow(areaData.commercial, commercialType(unit.useType))
      const residentialValueRow = findRow(areaData.residentialValue, residentialValueType(unit.useType))
      const residentialRentRow = findResidentialRentRow(
        areaData.residentialRent,
        null,
        unit.useType,
      )
      const subjectComparable = subjectComparableForUnit(subjectComparables, unit.sourceUnitId)
      const suggestions = {
        suggestedSqftYear: marketValue(commercialRow?.rent),
        suggestedMonthlyRent: marketValue(residentialRentRow?.rent),
        suggestedAdr: '',
        suggestedValue: subjectComparable
          ? marketValue(subjectComparable.indicatedValue)
          : marketValue(residentialValueRow?.soldPrice),
        suggestedSqftValue: subjectComparable
          ? marketValue(subjectComparable.pricePerSqFt)
          : marketValue(residentialValueRow?.sqft),
      }

      return {
        ...applySuggestedAndAdopted(unit, unitInput, suggestions, [
          ['suggestedSqftYear', 'adoptedSqftYear'],
          ['suggestedMonthlyRent', 'adoptedMonthlyRent'],
          ['suggestedAdr', 'adoptedAdr'],
          ['suggestedValue', 'adoptedValue'],
          ['suggestedSqftValue', 'adoptedSqftValue'],
        ]),
        comparableSource: comparableSource(subjectComparable),
      }
    }),
  }
}

function residentialSubjectComparable(subjectComparables, enrichment, state) {
  const residentialUnits = (enrichment?.units ?? []).filter((unit) => residentialValueType(unit.type))
  const targetPropertyType = residentialValueType(state.propertyType)
  const targetBedroomCount = normalizedBedroomCount(state.bedroomCount)
  const matchingUnit = residentialUnits.find((unit) => (
    residentialValueType(unit.type) === targetPropertyType &&
    normalizedBedroomCount(unit.bedroomCount || unit.type) === targetBedroomCount
  ))

  return subjectComparableForUnit(subjectComparables, matchingUnit?.id)
}

function subjectComparableForUnit(subjectComparables, unitId) {
  if (!unitId) return null

  const comparable = (subjectComparables?.units ?? []).find((unit) => (
    String(unit.unitId ?? '') === String(unitId)
  ))
  if (!comparable || comparable.status !== 'supported' || !positiveNumber(comparable.indicatedValue)) {
    return null
  }

  return comparable
}

function comparableSource(subjectComparable) {
  if (!subjectComparable) return 'activation_market_scan'

  return `subject_${subjectComparable.fallbackLevel || 'exact'}`
}

function applySuggestedAndAdopted(state, input, suggestions, pairs) {
  return pairs.reduce((nextState, [suggestedKey, adoptedKey]) => ({
    ...nextState,
    [suggestedKey]: suggestions[suggestedKey],
    [adoptedKey]: adoptedValue(input, adoptedKey, suggestions[suggestedKey]),
  }), state)
}

function adoptedValue(input, key, suggestion) {
  return Object.hasOwn(input, key) ? input[key] : suggestion
}

function preserveEditedAdoptedValues(previousState, refreshedState, pairs) {
  return pairs.reduce((nextState, [suggestedKey, adoptedKey]) => {
    if (previousState[adoptedKey] === previousState[suggestedKey]) {
      return { ...nextState, [adoptedKey]: refreshedState[suggestedKey] }
    }

    return { ...nextState, [adoptedKey]: previousState[adoptedKey] }
  }, refreshedState)
}

function preserveEditedUnitValues(previousUnit, refreshedUnit) {
  if (!previousUnit) return refreshedUnit

  return preserveEditedAdoptedValues(previousUnit, refreshedUnit, [
    ['suggestedSqftYear', 'adoptedSqftYear'],
    ['suggestedMonthlyRent', 'adoptedMonthlyRent'],
    ['suggestedAdr', 'adoptedAdr'],
    ['suggestedValue', 'adoptedValue'],
    ['suggestedSqftValue', 'adoptedSqftValue'],
  ])
}

function preserveEditedRoomRent(previousRoom, refreshedRoom) {
  if (!previousRoom) return { ...refreshedRoom, adoptedRent: refreshedRoom.suggestedRent }

  return preserveEditedAdoptedValues(previousRoom, refreshedRoom, [
    ['suggestedRent', 'adoptedRent'],
  ])
}

function initialResidentialType(enrichment, persistedType) {
  if (persistedType) return persistedType

  const residentialUnit = enrichment?.units?.find((unit) => residentialValueType(unit.type))
  return residentialUnit?.type ?? DEFAULT_BRRR_STATE.propertyType
}

function initialBedroomCount(enrichment, input, propertyType) {
  const persistedCount = normalizedBedroomCount(input.bedroomCount)
  if (persistedCount) return persistedCount

  const enrichmentCount = normalizedBedroomCount(enrichment?.enrichment?.bedroomCount)
  return enrichmentCount || normalizedBedroomCount(residentialRentType(propertyType))
}

function marketDataForPostcode(marketData, postcodeArea) {
  const entry = Object.entries(marketData ?? {}).find(([postcode]) => (
    normalize(postcode) === normalize(postcodeArea)
  ))

  return entry?.[1] ?? {}
}

function residentialValueType(type) {
  const value = normalize(type)
  if (value.includes('flat') || value === 'studio') return 'Flat'
  if (value.includes('terraced')) return 'Terraced House'
  if (value.includes('semi-detached') || value.includes('semi detached')) return 'Semi-Detached House'
  if (value.includes('detached')) return 'Detached House'

  return null
}

function residentialRentType(type) {
  const normalizedType = normalize(type)
  const bedrooms = normalizedType.match(/\b(\d+)[- ]?bed\b/)?.[1] ||
    normalizedType.match(/^\d+$/)?.[0]

  return bedrooms ? `${bedrooms}-Bed` : null
}

function findResidentialRentRow(rows, bedroomCount, propertyType) {
  return findRow(rows, residentialRentType(bedroomCount)) ||
    findRow(rows, residentialRentType(propertyType)) ||
    findRow(rows, propertyType)
}

function normalizedBedroomCount(value) {
  const count = String(value ?? '').match(/\d+/)?.[0]

  return count ? String(Number(count)) : ''
}

function hmoType(type) {
  const normalized = normalize(type)
  return HMO_TYPE_ALIASES[normalized] ?? normalized
}

function commercialType(type) {
  const normalized = normalize(type)
  return COMMERCIAL_TYPE_ALIASES[normalized] ?? normalized
}

function findRow(rows, type) {
  if (!type) return null
  const normalizedType = normalize(type)

  return (rows ?? []).find((row) => normalize(row.type) === normalizedType) ?? null
}

function rowsFromInput(rows, defaults) {
  const source = Array.isArray(rows) ? rows : defaults
  return source.map((row) => ({ ...row }))
}

function incomeScheduleUnits(units, areaData, isCoWork = false) {
  if (!Array.isArray(units) || units.length === 0) {
    return rowsFromInput(null, INITIAL_COMMERCIAL_UNITS)
      .map((unit) => withCommercialUnitOpexDefault(unit, isCoWork, true))
  }

  return units.map((unit, index) => {
    const useType = appraisalUseType(unit.type)
    const isResidential = Boolean(residentialValueType(useType))
    const valueRow = findRow(areaData.residentialValue, residentialValueType(useType))
    const hasSizeBasedValue = Boolean(positiveNumber(unit.area) && positiveNumber(valueRow?.sqft))

    return {
      id: `income-${unit.id ?? index + 1}`,
      sourceUnitId: unit.id ?? null,
      sourceUpdatedAt: unit.updatedAt ?? null,
      sourceStatus: unit.status ?? null,
      currentMonthlyRent: unit.rent ?? null,
      currentAnnualRent: unit.annualRent ?? null,
      name: unit.unit || `Unit ${index + 1}`,
      useType,
      sizeSqft: marketValue(unit.area),
      suggestedSqftYear: '',
      adoptedSqftYear: '',
      opexPercent: commercialUnitOpexDefault({ useType, operatingModel: 'AST', isCoWork }),
      adoptedYield: isResidential ? '7' : '8.5',
      suggestedMonthlyRent: '',
      adoptedMonthlyRent: '',
      suggestedAdr: '',
      adoptedAdr: '',
      occupancy: isResidential ? '80' : '75',
      suggestedValue: '',
      adoptedValue: '',
      suggestedSqftValue: '',
      adoptedSqftValue: '',
      valuationBasis: incomeUnitValuationBasis(isResidential, hasSizeBasedValue),
      operatingModel: 'AST',
    }
  })
}

function withCommercialUnitOpexDefault(unit, isCoWork, replaceDefault = false) {
  const defaultValue = commercialUnitOpexDefault({
    useType: unit.useType,
    operatingModel: unit.operatingModel,
    isCoWork,
  })

  return {
    ...unit,
    opexPercent: replaceDefault ? defaultValue : unit.opexPercent ?? defaultValue,
  }
}

function incomeUnitValuationBasis(isResidential, hasSizeBasedValue) {
  if (!isResidential) return 'Commercial'
  if (hasSizeBasedValue) return '£/sqft'
  return 'Comparable'
}

function appraisalUseType(type) {
  const value = String(type ?? '').trim()
  const normalized = normalize(value)

  if (!value) return 'Other Commercial'
  if (normalized === 'office' || normalized === 'offices') return 'Office'
  if (normalized === 'industrial' || normalized === 'warehouse') return 'Industrial / Warehouse'
  if (normalized === 'commercial') return 'Other Commercial'

  return value
}

function rowTypeSignature(rows) {
  return (rows ?? []).map((row) => `${row.id}:${row.type ?? row.useType ?? ''}`).join('|')
}

function marketValue(value) {
  if (value === null || value === undefined || value === '') return ''

  const number = Number(String(value).replaceAll(',', ''))
  if (!Number.isFinite(number)) return String(value)

  return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(number)
}

function suggestedResidentialGdv(valueRow, internalArea) {
  const pricePerSqFt = positiveNumber(valueRow?.sqft)
  const floorArea = positiveNumber(internalArea)

  if (pricePerSqFt && floorArea) return marketValue(pricePerSqFt * floorArea)

  return marketValue(valueRow?.soldPrice)
}

function positiveNumber(value) {
  const number = Number(String(value ?? '').replaceAll(',', ''))

  return Number.isFinite(number) && number > 0 ? number : null
}

function normalize(value) {
  return String(value ?? '').trim().toLowerCase()
}
